/**
 * Lattice types + free parameters for the fundamental-domain engine.
 *
 * Geometry now lives in fundamental.ts (each WPG owns its fundamental-domain
 * polygon). This module is just the lattice-parameter model: which free
 * parameters a lattice exposes, their ranges, and defaults.
 */

export type LatticeType =
  | 'oblique'
  | 'rectangular'
  | 'centred-rect'
  | 'square'
  | 'hexagonal';

export interface LatticeParams {
  /** b/a — rectangular and oblique lattices only. */
  aspectRatio?: number;
  /** degrees — oblique and centred-rectangular (rhombus) lattices only. */
  angle?: number;
  /**
   * Hexagonal lattice only (Bug 0). The "3-way rhombus" fundamental domain
   * (fundamental.ts's `spec('p3', …)`) is a fixed 120° wedge of the hexagon —
   * this rotates *which* wedge is used 60° anticlockwise from the original.
   * Absent (older saved favourites, which never had this field) ⇒ the
   * original wedge, so existing saved work renders unchanged; new configs get
   * `true` from defaultParams() below. Never toggled by anything else — this
   * lattice has no other free parameters (PARAM_RANGES.hexagonal is empty).
   */
  rhombusRotated?: boolean;
}

/** Free-parameter ranges per lattice type. */
export const PARAM_RANGES: Record<
  LatticeType,
  { aspectRatio?: [number, number]; angle?: [number, number] }
> = {
  oblique: { aspectRatio: [0.4, 2.5], angle: [20, 160] },
  rectangular: { aspectRatio: [0.25, 4.0] },
  'centred-rect': { angle: [30, 150] },
  square: {},
  hexagonal: {},
};

export function defaultParams(lattice: LatticeType): LatticeParams {
  switch (lattice) {
    case 'oblique':
      return { aspectRatio: 1, angle: 75 };
    case 'rectangular':
      return { aspectRatio: 1.6 };
    case 'centred-rect':
      return { angle: 60 };
    case 'hexagonal':
      return { rhombusRotated: true }; // Bug 0 — new default only, see LatticeParams
    default:
      return {};
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Clamp params to the valid range for the lattice; fill missing with defaults. */
export function normaliseParams(lattice: LatticeType, params: LatticeParams): LatticeParams {
  const range = PARAM_RANGES[lattice];
  const out: LatticeParams = {};
  if (range.aspectRatio) {
    out.aspectRatio = clamp(params.aspectRatio ?? defaultParams(lattice).aspectRatio ?? 1, ...range.aspectRatio);
  }
  if (range.angle) {
    out.angle = clamp(params.angle ?? defaultParams(lattice).angle ?? 90, ...range.angle);
  }
  // Bug 0: unlike aspectRatio/angle above, a *missing* value here must mean
  // "this config predates the field" (⇒ the original orientation), not "fall
  // back to today's default" — otherwise every already-saved favourite would
  // silently pick up the new orientation too. Only brand-new params objects
  // (built from defaultParams()/defaultLatticeParams() at creation time) ever
  // carry `rhombusRotated: true` explicitly.
  if (lattice === 'hexagonal') {
    out.rhombusRotated = params.rhombusRotated ?? false;
  }
  return out;
}
